import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  Loader2,
  PackageCheck,
  RefreshCcw,
  ShoppingCart,
} from "lucide-react";
import PageContainer from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { useLocalization } from "@/lib/localization";
import { useAuth } from "@/lib/store";
import { fetchAnalyticsRestockPlan } from "@/lib/shopApi";
import { formatOperatingCurrency } from "@/lib/money";

function normalizeRestockPlan(response) {
  return response?.restockPlan ?? null;
}

function formatCurrencyLocal(value, locale) {
  return formatOperatingCurrency(value, locale);
}

function formatGeneratedAt(value, locale, copy) {
  if (!value) return copy.livePlan;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return copy.livePlan;
  return copy.updatedAt(
    new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date),
  );
}

export default function AnalyticsRestockPlanner() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { locale, messages } = useLocalization();
  const copy = messages.analytics.restockPlanner;
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const loadPlan = useCallback(async () => {
    if (!session?.sessionId) {
      setError(messages.analytics.sessionNotReady);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await fetchAnalyticsRestockPlan(session.sessionId);
      setPlan(normalizeRestockPlan(response));
    } catch (err) {
      setError(err?.message || copy.errorTitle);
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [copy.errorTitle, messages.analytics.sessionNotReady, session?.sessionId]);

  useEffect(() => {
    void loadPlan();
  }, [loadPlan]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const recommendations = useMemo(
    () => plan?.recommendations ?? [],
    [plan?.recommendations],
  );
  const hasUrgentItem = recommendations.some((item) => item.urgency === "high" && (item.currentStock === 0 || item.dataConfidence !== "low"));
  const summaryTone = hasUrgentItem
    ? copy.summaryNeedsAttention
    : recommendations.length
      ? copy.summaryMonitor
      : copy.summaryHealthy;
  const nextSteps = recommendations.length
    ? copy.withDataSteps
    : copy.withoutDataSteps;
  const totalEstimatedCost = useMemo(
    () =>
      recommendations.reduce((sum, item) => sum + (item.estimatedCost || 0), 0),
    [recommendations],
  );

  return (
    <PageContainer className="px-4 py-6 sm:px-6 lg:px-8">
      <section className="mx-auto w-full max-w-3xl">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 mb-3 h-8 px-2 text-muted-foreground hover:text-foreground"
              onClick={() => navigate("/analytics")}
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {messages.analytics.analyticsBack}
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">{copy.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {copy.subtitle}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            disabled={loading}
            onClick={loadPlan}
          >
            <RefreshCcw
              className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`}
            />
            {messages.analytics.refresh}
          </Button>
        </div>

        {loading ? (
          <div className="luxe-card rounded-lg p-6">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              {copy.loading}
            </div>
          </div>
        ) : null}

        {!loading && error ? (
          <div className="luxe-card rounded-lg border-destructive/25 p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-semibold tracking-tight">
                  {copy.errorTitle}
                </h2>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  {error}
                </p>
                <Button className="mt-4 h-9" onClick={loadPlan}>
                  {messages.analytics.tryAgain}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {!loading && !error && plan ? (
          <div className="space-y-4">
            <div className="luxe-card rounded-lg p-5 sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-warning/10 text-warning ring-1 ring-warning/15">
                    <ShoppingCart className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="inline-flex items-center rounded-full border border-border/70 bg-background/70 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                      {summaryTone}
                    </div>
                    <h2 className="mt-1 text-xl font-semibold tracking-tight">
                      {recommendations.length
                        ? copy.productsToCheck(recommendations.length)
                        : copy.stockLooksHealthy}
                    </h2>
                    <p className="mt-1 max-w-xl text-sm leading-5 text-muted-foreground">
                      {recommendations.length
                        ? copy.productsToCheckDescription
                        : copy.stockHealthyDescription}
                    </p>
                  </div>
                </div>
                <div className="pt-1 text-sm text-muted-foreground sm:text-right">
                  {formatGeneratedAt(plan.generatedAt, locale, copy)}
                </div>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <MiniStat
                  icon={<CalendarClock className="h-4 w-4" />}
                  label={copy.supplierLeadTime}
                  value={`${plan.leadTimeDays ?? 0} day${plan.leadTimeDays === 1 ? "" : "s"}`}
                />
                <MiniStat
                  icon={<PackageCheck className="h-4 w-4" />}
                  label={copy.safetyStock}
                  value={`${plan.strategy?.safetyStockDays ?? 0} day${plan.strategy?.safetyStockDays === 1 ? "" : "s"}`}
                />
                <MiniStat
                  icon={<ShoppingCart className="h-4 w-4" />}
                  label={copy.estimatedBuy}
                  value={formatCurrencyLocal(totalEstimatedCost, locale)}
                />
              </div>
            </div>

            <div className="relative py-1">
              <div className="absolute bottom-0 left-[22px] top-0 hidden w-px bg-border sm:block" />
              {nextSteps.map((step, index) => (
                <FlowStep key={step} index={index + 1} text={step} />
              ))}
            </div>

            <div className="luxe-card rounded-lg px-5 py-5 sm:px-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {copy.detailedView}
                  </p>
                  <h2 className="mt-1 text-base font-semibold tracking-tight">
                    {recommendations.length
                      ? copy.openFullList
                      : copy.reviewList}
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    {recommendations.length
                      ? copy.fullListDescription(recommendations.length)
                      : copy.reviewListDescription}
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="h-10 rounded-md border-border/70 bg-background/90 px-4 text-[13px] font-medium text-foreground shadow-none transition-colors hover:border-border hover:bg-accent/30 hover:text-foreground"
                  onClick={() => navigate("/analytics/restock-plan/list")}
                >
                  {copy.viewRestockList}
                  <ArrowRight className="ml-1 h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </section>
    </PageContainer>
  );
}

function MiniStat({ icon, label, value }) {
  return (
    <div className="rounded-md border border-border/70 bg-background/60 px-3 py-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-2 font-mono text-sm font-semibold tabular-nums text-foreground">
        {value}
      </div>
    </div>
  );
}

function FlowStep({ index, text }) {
  return (
    <div className="relative flex gap-4 py-2 sm:pl-0">
      <div className="z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-border/70 bg-card font-mono text-sm font-semibold text-primary shadow-sm">
        {index}
      </div>
      <div className="luxe-card flex min-h-11 flex-1 items-center rounded-lg px-4 py-3">
        <p className="text-sm leading-5 text-muted-foreground">{text}</p>
      </div>
    </div>
  );
}
